/**
 * Free, no-login tools catalog (`/herramientas-gratuitas/:slug`).
 *
 * Single declarative source for the UI copy, FAQ and long-form SEO text. The
 * server keeps a parallel list of SEO strings in `server/src/seo/toolsData.ts`
 * — keep the slugs in sync between the two.
 *
 * The interactive widget for each tool is resolved by slug in
 * `components/tools/registry.tsx`.
 */
import type { LucideIcon } from 'lucide-react';
import { Type, Clock, TrendingUp, User, Hash, PenLine, Lightbulb } from 'lucide-react';

export type ToolKind = 'static' | 'ai';

export type Tool = {
  slug: string;
  kind: ToolKind;
  icon: LucideIcon;
  /** Small uppercase label / category. */
  eyebrow: string;
  /** H1 on the tool page. */
  title: string;
  /** One-liner for the hub cards. */
  shortDescription: string;
  /** Hero subtitle on the tool page. */
  description: string;
  /** Long-form intro rendered below the widget (crawlable SEO copy). */
  intro: string;
  howItWorks: { title: string; body: string }[];
  faq: { q: string; a: string }[];
};

export const TOOLS: Tool[] = [
  {
    slug: 'contador-de-caracteres',
    kind: 'static',
    icon: Type,
    eyebrow: 'Texto',
    title: 'Contador de caracteres por red social',
    shortDescription: 'Cuenta caracteres en tiempo real y ve el límite de cada red mientras escribes.',
    description:
      'Escribe o pega tu texto y cuéntalo al instante. Te mostramos cuántos caracteres llevas y el límite de Instagram, X, TikTok, LinkedIn, Facebook y Threads.',
    intro:
      'Cada red social tiene su propio límite de caracteres y quedarte corto —o pasarte— puede arruinar un buen post. Este contador de caracteres gratuito cuenta letras, palabras y caracteres restantes en tiempo real para Instagram, X (Twitter), TikTok, LinkedIn, Facebook y Threads, para que adaptes tu copy a cada plataforma sin sorpresas. No necesitas registrarte.',
    howItWorks: [
      { title: 'Escribe o pega', body: 'Introduce tu caption, tuit o descripción en el cuadro de texto.' },
      { title: 'Mira los límites', body: 'Verás caracteres y palabras al instante, con el límite de cada red.' },
      { title: 'Ajusta y publica', body: 'Recorta lo que sobre y lleva el texto a tu programador favorito.' },
    ],
    faq: [
      { q: '¿Cuántos caracteres admite cada red?', a: 'X permite 280 (25.000 con Premium), Instagram 2.200 en el caption, TikTok 2.200, LinkedIn 3.000, Facebook 63.206 y Threads 500. Los mostramos todos en vivo.' },
      { q: '¿Cuenta los espacios y emojis?', a: 'Sí. Contamos cada carácter visible, incluidos espacios y emojis, igual que lo hacen las redes.' },
      { q: '¿Es gratis y sin registro?', a: 'Totalmente. Todo ocurre en tu navegador, no guardamos tu texto.' },
    ],
  },
  {
    slug: 'mejor-hora-para-publicar',
    kind: 'static',
    icon: Clock,
    eyebrow: 'Estrategia',
    title: 'Mejor hora para publicar en redes sociales',
    shortDescription: 'Las mejores franjas por red y día, ajustadas a tu zona horaria.',
    description:
      'Consulta las mejores horas para publicar en Instagram, TikTok, X, LinkedIn y Facebook según el día de la semana y tu zona horaria. Una guía práctica para no publicar a ciegas.',
    intro:
      'Publicar buen contenido a la hora equivocada es tirar alcance a la basura. Estas franjas se basan en patrones generales de actividad por red y día de la semana, y se ajustan a tu zona horaria. Úsalas como punto de partida y luego valida con tus propias analíticas: la mejor hora siempre es cuando TU audiencia está conectada.',
    howItWorks: [
      { title: 'Elige red y zona horaria', body: 'Selecciona la plataforma y tu zona horaria.' },
      { title: 'Mira las franjas', body: 'Te mostramos las mejores horas por día de la semana.' },
      { title: 'Programa con cabeza', body: 'Lleva esas franjas a la cola inteligente de Zettapost.' },
    ],
    faq: [
      { q: '¿De dónde salen estas horas?', a: 'De patrones generales de actividad por red. Son un punto de partida sólido; tus analíticas mandan.' },
      { q: '¿Sirve para España y LatAm?', a: 'Sí. Ajusta la zona horaria y las franjas se recalculan a tu hora local.' },
      { q: '¿Cómo encuentro mi mejor hora real?', a: 'Con Zettapost puedes ver cuándo tu audiencia interactúa más y programar en esas franjas automáticamente.' },
    ],
  },
  {
    slug: 'calculadora-de-engagement',
    kind: 'static',
    icon: TrendingUp,
    eyebrow: 'Analítica',
    title: 'Calculadora de engagement para Instagram y otras redes',
    shortDescription: 'Mide tu engagement real a partir de seguidores, likes y comentarios.',
    description:
      'Introduce tus seguidores y la interacción media de tus posts y calcula tu tasa de engagement al instante. Descubre si tu cuenta engancha de verdad y compárate con los rangos del sector.',
    intro:
      'La tasa de engagement mide qué porcentaje de tu audiencia interactúa con tu contenido y es la métrica que de verdad importa, más que el número de seguidores. Esta calculadora gratuita la obtiene a partir de tus seguidores y la interacción media (likes + comentarios + guardados) por publicación, y te dice si tu cuenta está por encima o por debajo de la media. La fórmula es sencilla: interacciones medias por publicación ÷ seguidores × 100. Por ejemplo, si tus posts reciben de media 120 interacciones y tienes 4.000 seguidores, tu tasa de engagement es del 3 %.',
    howItWorks: [
      { title: 'Mete tus números', body: 'Seguidores e interacción media por post (likes, comentarios…).' },
      { title: 'Obtén tu tasa', body: 'Calculamos tu engagement rate en porcentaje al instante.' },
      { title: 'Interpreta', body: 'Te decimos si está bajo, en la media o es excelente.' },
    ],
    faq: [
      { q: '¿Cómo se calcula el engagement?', a: 'Sumamos las interacciones medias por post y las dividimos entre tus seguidores, en porcentaje.' },
      { q: '¿Qué tasa es buena?', a: 'Depende del tamaño: cuentas pequeñas suelen tener más %. Como referencia, por encima del 3% en Instagram es muy bueno.' },
      { q: '¿Sirve para Instagram y TikTok?', a: 'Sí, el cálculo es válido para cualquier red; solo cambian los rangos de referencia.' },
      { q: '¿Cuál es la fórmula del engagement rate?', a: '(Likes + comentarios + guardados + compartidos de una publicación) ÷ seguidores × 100. Para una cuenta, usa la media de tus últimas 10-12 publicaciones: un post suelto que se viraliza o que no funciona distorsiona el resultado.' },
      { q: '¿Engagement sobre seguidores o sobre alcance?', a: 'Sobre seguidores es el que se usa para compararse con otras cuentas, porque ese dato es público. Sobre alcance (interacciones ÷ personas alcanzadas) mide mejor cada publicación, pero solo lo ves en las estadísticas de tu propia cuenta.' },
      { q: '¿Cómo puedo mejorar mi tasa de engagement?', a: 'Publica con regularidad, empieza cada post con un gancho, termina con una pregunta o una llamada a la acción, responde a los comentarios en la primera hora y mira qué formatos te funcionan para repetirlos.' },
    ],
  },
  {
    slug: 'generador-de-bio',
    kind: 'ai',
    icon: User,
    eyebrow: 'IA',
    title: 'Generador de bio para Instagram y TikTok',
    shortDescription: 'Crea una bio con gancho en segundos con IA. Varias opciones para copiar.',
    description:
      'Describe tu cuenta y nuestra IA te genera varias biografías listas para tu Instagram, TikTok o link in bio: con gancho, personalidad y llamada a la acción.',
    intro:
      'Tu bio es lo primero que ve quien llega a tu perfil y decide si te sigue. Este generador de bio con IA crea varias opciones en español a partir de una breve descripción de tu cuenta o negocio, equilibrando personalidad, claridad y una llamada a la acción. Cópiala, ajústala a tu voz y pégala en tu perfil o en tu link in bio.',
    howItWorks: [
      { title: 'Describe tu cuenta', body: 'Cuéntanos a qué te dedicas, tu tono y qué ofreces.' },
      { title: 'Genera con IA', body: 'Obtienes varias bios distintas en segundos.' },
      { title: 'Copia y publica', body: 'Elige tu favorita, ajústala y pégala en tu perfil.' },
    ],
    faq: [
      { q: '¿Para qué redes sirve?', a: 'Para Instagram, TikTok, X, LinkedIn y cualquier link in bio. Adapta el tono a la red elegida.' },
      { q: '¿Es gratis?', a: 'Sí. Puedes generar varias veces al día gratis y sin registro. Para uso ilimitado, crea una cuenta.' },
      { q: '¿Puedo editar el resultado?', a: 'Claro. La IA te da un punto de partida potente; dale tu toque personal antes de publicar.' },
    ],
  },
  {
    slug: 'generador-de-hashtags',
    kind: 'ai',
    icon: Hash,
    eyebrow: 'IA',
    title: 'Generador de hashtags con IA',
    shortDescription: 'Hashtags relevantes que mezclan alcance y nicho, a partir de tu tema.',
    description:
      'Escribe tu tema o describe tu post y la IA te genera una lista de hashtags relevantes, mezclando los más grandes con los de nicho para maximizar tu alcance.',
    intro:
      'Los hashtags adecuados ayudan a que el contenido llegue a quien aún no te sigue. Este generador de hashtags con IA crea una selección a partir de tu tema, combinando hashtags de mucho alcance con otros de nicho (más fáciles de posicionar). Cópialos y úsalos en tus publicaciones de Instagram, TikTok o X.',
    howItWorks: [
      { title: 'Describe tu post', body: 'Escribe el tema o una breve descripción del contenido.' },
      { title: 'Genera la lista', body: 'La IA te devuelve hashtags grandes y de nicho.' },
      { title: 'Copia y usa', body: 'Copia todos con un clic y pégalos en tu publicación.' },
    ],
    faq: [
      { q: '¿Cuántos hashtags debo usar?', a: 'En Instagram, entre 5 y 15 bien elegidos suele rendir mejor que el máximo de 30. En TikTok, 3-5.' },
      { q: '¿Mezcla hashtags grandes y pequeños?', a: 'Sí, esa es la clave: combinamos alcance amplio con nichos donde es más fácil destacar.' },
      { q: '¿Es gratis y sin registro?', a: 'Sí, con un límite de usos diarios. Crea una cuenta para generar sin límite.' },
    ],
  },
  {
    slug: 'generador-de-captions',
    kind: 'ai',
    icon: PenLine,
    eyebrow: 'IA',
    title: 'Generador de descripciones para Instagram con IA',
    shortDescription: 'Captions con gancho y CTA para tus posts, en el tono que elijas.',
    description:
      'Cuéntale a la IA de qué trata tu post y elige el tono: te escribe una descripción con un gancho potente, cuerpo con valor, llamada a la acción y hashtags.',
    intro:
      'El caption decide si alguien se para a leer o sigue haciendo scroll. Este generador de descripciones con IA escribe captions en español para Instagram, TikTok, LinkedIn y más, con un gancho inicial fuerte, un cuerpo con valor y una llamada a la acción clara, además de hashtags sugeridos. Tú eliges el tono.',
    howItWorks: [
      { title: 'Describe el post', body: 'Cuéntanos el tema y elige la red y el tono.' },
      { title: 'Genera el caption', body: 'La IA escribe la descripción con gancho, cuerpo, CTA y hashtags.' },
      { title: 'Publícalo', body: 'Cópialo o llévalo directo a Zettapost para programarlo.' },
    ],
    faq: [
      { q: '¿Puedo elegir el tono?', a: 'Sí: cercano, profesional, divertido, inspirador… Adaptamos el texto al tono que indiques.' },
      { q: '¿Incluye hashtags?', a: 'Sí, cada caption viene con una selección de hashtags relevantes que puedes copiar.' },
      { q: '¿Es gratis?', a: 'Sí, con un límite diario de generaciones y sin registrarte. Para uso ilimitado, crea tu cuenta de Zettapost (3 días de prueba gratis).' },
      { q: '¿Sirve para TikTok y LinkedIn?', a: 'Sí. Elige la red y el texto se adapta: más corto y directo para TikTok, más profesional para LinkedIn.' },
    ],
  },
  {
    slug: 'generador-de-ideas',
    kind: 'ai',
    icon: Lightbulb,
    eyebrow: 'IA',
    title: 'Generador de ideas de contenido con IA',
    shortDescription: 'Ideas con gancho, ángulo y ejemplo de caption para tu nicho.',
    description:
      'Dinos tu nicho y la IA te propone ideas de contenido accionables: cada una con un gancho llamativo, un ángulo claro y un ejemplo de caption para empezar.',
    intro:
      'El bloqueo creativo es el mayor enemigo de la constancia en redes. Este generador de ideas de contenido con IA te da ideas listas para tu nicho, cada una con un gancho que para el scroll, un ángulo o formato concreto y un ejemplo de caption. Úsalo para llenar tu calendario de contenido de Instagram, TikTok o LinkedIn.',
    howItWorks: [
      { title: 'Indica tu nicho', body: 'Escribe el tema o sector de tu cuenta y elige la red.' },
      { title: 'Genera ideas', body: 'La IA te propone ideas con gancho, ángulo y caption de ejemplo.' },
      { title: 'Planifica', body: 'Elige tus favoritas y prográmalas en tu calendario.' },
    ],
    faq: [
      { q: '¿Las ideas sirven para mi sector?', a: 'Sí. Cuanto más concreto sea tu nicho, más afinadas serán las ideas que genera la IA.' },
      { q: '¿Me da el caption hecho?', a: 'Cada idea incluye un ejemplo de caption para que arranques sin partir de cero.' },
      { q: '¿Es gratis y sin registro?', a: 'Sí, con un límite de usos al día. Crea una cuenta gratis para generar sin límite.' },
    ],
  },
];

export function getToolBySlug(slug: string): Tool | undefined {
  return TOOLS.find((t) => t.slug === slug);
}
